import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Page,
  Layout,
  Card,
  ResourceList,
  ResourceItem,
  Text,
  Button,
  Badge,
  EmptyState,
  Banner,
  Spinner,
  DropZone,
  Thumbnail,
  BlockStack,
  InlineStack,
  Modal,
  Box,
  TextField,
  Select,
  Tabs,
} from "@shopify/polaris";
import { useNavigate } from "react-router-dom";
import { useShop } from "../context/ShopContext.jsx";
import { useI18n } from "../context/I18nContext.jsx";

// ── Search ──────────────────────────────────────────────────────────────────
/**
 * Practical product search: every query word must appear in at least one of
 * the searchable fields (title, productType, tags, vendor, id).
 * Returns { match: boolean, score: number }.
 */
function searchProduct(product, query) {
  if (!query) return { match: true, score: 0 };
  const q = query.toLowerCase().trim();
  if (!q) return { match: true, score: 0 };

  // Build one big searchable string per product
  const haystack = [
    product.title || "",
    product.productType || "",
    product.tags || "",
    product.vendor || "",
    String(product.id || ""),
    product.handle || "",
  ]
    .join(" ")
    .toLowerCase();

  // Exact full-query substring → highest score
  if (haystack.includes(q)) return { match: true, score: 100 };

  // Split query into words — every word must appear somewhere in haystack
  const words = q.split(/\s+/).filter(Boolean);
  const matchedWords = words.filter((w) => haystack.includes(w));

  if (matchedWords.length === words.length) {
    // All words found
    return { match: true, score: 90 };
  }

  if (matchedWords.length > 0 && matchedWords.length >= words.length * 0.5) {
    // At least half the words matched (helpful for partial typing)
    return { match: true, score: 50 + (matchedWords.length / words.length) * 30 };
  }

  // Single-word typo tolerance: for each query word, check if any word in
  // haystack starts with it (prefix match) or is close (1-char difference)
  if (words.length === 1 && q.length >= 2) {
    const haystackWords = haystack.split(/\s+/);
    for (const hw of haystackWords) {
      if (hw.startsWith(q)) return { match: true, score: 70 };
      if (q.length >= 3 && hw.includes(q)) return { match: true, score: 65 };
    }
    // Levenshtein-like: allow 1 edit for words of length 4+
    if (q.length >= 4) {
      for (const hw of haystackWords) {
        if (Math.abs(hw.length - q.length) <= 1 && editDistance1(hw, q)) {
          return { match: true, score: 40 };
        }
      }
    }
  }

  // Multi-word: check prefix matches for each query word
  if (words.length > 1) {
    const haystackWords = haystack.split(/\s+/);
    const prefixMatches = words.filter((w) =>
      haystackWords.some((hw) => hw.startsWith(w))
    );
    if (prefixMatches.length === words.length) {
      return { match: true, score: 75 };
    }
    if (prefixMatches.length > 0 && prefixMatches.length >= words.length * 0.5) {
      return { match: true, score: 40 + (prefixMatches.length / words.length) * 20 };
    }
  }

  return { match: false, score: 0 };
}

/** Check if two strings differ by at most 1 edit (substitution only, for speed) */
function editDistance1(a, b) {
  if (Math.abs(a.length - b.length) > 1) return false;
  let diffs = 0;
  const len = Math.min(a.length, b.length);
  for (let i = 0; i < len; i++) {
    if (a[i] !== b[i]) diffs++;
    if (diffs > 1) return false;
  }
  return diffs + Math.abs(a.length - b.length) <= 1;
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(",")[1];
      resolve({ base64, mimeType: file.type });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const SORT_OPTIONS_KEYS = ["newest", "oldest", "alpha_asc", "alpha_desc", "price_asc", "price_desc", "variants", "updated"];

const TAB_OPTION_IDS = ["all", "with_fitting", "without_fitting", "active", "draft"];

export default function ProductFittingPage() {
  const navigate = useNavigate();
  const { connected, loading: shopLoading, apiFetch } = useShop();
  const { t } = useI18n();

  const SORT_OPTIONS = useMemo(() => [
    { label: t("products.sortNewest"), value: "newest" },
    { label: t("products.sortOldest"), value: "oldest" },
    { label: t("products.sortAZ"), value: "alpha_asc" },
    { label: t("products.sortZA"), value: "alpha_desc" },
    { label: t("products.sortPriceLow"), value: "price_asc" },
    { label: t("products.sortPriceHigh"), value: "price_desc" },
  ], [t]);

  const TAB_OPTIONS = useMemo(() => [
    { id: "all", content: t("products.tabAll") },
    { id: "with_fitting", content: t("products.tabWithPhoto") },
    { id: "without_fitting", content: t("products.tabWithoutPhoto") },
    { id: "active", content: t("products.tabActive") },
    { id: "draft", content: t("products.tabDraft") },
  ], [t]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [nextPageInfo, setNextPageInfo] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  // Search, filter, sort state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null); // null = not searching
  const [searching, setSearching] = useState(false);
  const [sortValue, setSortValue] = useState("newest");
  const [selectedTab, setSelectedTab] = useState(0);
  const [typeFilter, setTypeFilter] = useState("");

  // Modal state for uploading a garment photo
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [editFile, setEditFile] = useState(null);
  const [editPreview, setEditPreview] = useState(null);
  const [selectedExistingUrl, setSelectedExistingUrl] = useState(null);
  const [saving, setSaving] = useState(false);

  // Size chart modal state
  const [sizeChartOpen, setSizeChartOpen] = useState(false);
  const [sizeChartProduct, setSizeChartProduct] = useState(null);
  const [sizeChartData, setSizeChartData] = useState([]);
  const [sizeChartLoading, setSizeChartLoading] = useState(false);
  const [sizeChartSaving, setSizeChartSaving] = useState(false);
  const [sizeChartInherited, setSizeChartInherited] = useState(false);

  const loadProducts = useCallback(async (pageInfo = null) => {
    if (!connected) return;
    if (pageInfo) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    try {
      const url = pageInfo
        ? `/shopify-products?page_info=${pageInfo}`
        : "/shopify-products";
      const data = await apiFetch(url);
      if (pageInfo) {
        setProducts((prev) => [...prev, ...data.products]);
      } else {
        setProducts(data.products);
      }
      setNextPageInfo(data.pagination?.nextPageInfo || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [connected, apiFetch]);

  useEffect(() => {
    if (!shopLoading && connected) {
      loadProducts();
    } else if (!shopLoading && !connected) {
      setLoading(false);
    }
  }, [shopLoading, connected, loadProducts]);

  // Debounced server-side search
  useEffect(() => {
    const q = searchQuery.trim();
    if (!q || q.length < 2) {
      setSearchResults(null);
      setSearching(false);
      return;
    }
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const data = await apiFetch(`/shopify-products/search?q=${encodeURIComponent(q)}`);
        setSearchResults(data.products || []);
      } catch {
        setSearchResults(null);
      } finally {
        setSearching(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery, apiFetch]);

  // Product types for filter dropdown
  const productTypes = useMemo(() => {
    const types = [...new Set(products.map((p) => p.productType).filter(Boolean))];
    return [{ label: "Alle types", value: "" }, ...types.map((t) => ({ label: t, value: t }))];
  }, [products]);

  // Filtered and sorted products
  const filteredProducts = useMemo(() => {
    const tabId = TAB_OPTIONS[selectedTab]?.id || "all";

    // When server search results are available, use those as the base
    let base = searchResults !== null ? searchResults : products;

    let filtered = base.filter((p) => {
      // Tab filter
      if (tabId === "with_fitting" && !p.hasFitting) return false;
      if (tabId === "without_fitting" && p.hasFitting) return false;
      if (tabId === "active" && p.status !== "active") return false;
      if (tabId === "draft" && p.status !== "draft") return false;
      // Type filter
      if (typeFilter && p.productType !== typeFilter) return false;
      return true;
    });

    // When using server search, results are already relevance-sorted
    if (searchResults !== null) {
      return filtered;
    }

    // Sort (only when not searching)
    filtered = [...filtered].sort((a, b) => {
      switch (sortValue) {
        case "newest": return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        case "oldest": return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
        case "alpha_asc": return (a.title || "").localeCompare(b.title || "", "nl");
        case "alpha_desc": return (b.title || "").localeCompare(a.title || "", "nl");
        case "price_asc": return parseFloat(a.price || 0) - parseFloat(b.price || 0);
        case "price_desc": return parseFloat(b.price || 0) - parseFloat(a.price || 0);
        case "variants": return (b.variantCount || 0) - (a.variantCount || 0);
        case "updated": return new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0);
        default: return 0;
      }
    });

    return filtered;
  }, [products, searchResults, sortValue, selectedTab, typeFilter]);

  const handleDropZone = useCallback((_dropFiles, acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setEditFile(file);
      setEditPreview(URL.createObjectURL(file));
    }
  }, []);

  const openUploadModal = (product) => {
    setSelectedProduct(product);
    setEditFile(null);
    setEditPreview(product.fittingImageUrl || null);
    setSelectedExistingUrl(null);
    setModalOpen(true);
  };

  const handleSelectExisting = (src) => {
    setSelectedExistingUrl(src);
    setEditFile(null);
    setEditPreview(src);
  };

  const handleSave = async () => {
    if (!selectedProduct) return;
    setSaving(true);
    try {
      let garmentImageB64 = null;
      let garmentMimeType = "image/jpeg";
      if (editFile) {
        const result = await fileToBase64(editFile);
        garmentImageB64 = result.base64;
        garmentMimeType = result.mimeType;
      }

      if (!garmentImageB64 && !selectedExistingUrl) {
        setError(t("products.chooseOrUpload"));
        setSaving(false);
        return;
      }

      const body = {
        productTitle: selectedProduct.title,
        productImageUrl: selectedProduct.image,
        productHandle: selectedProduct.handle || null,
      };
      if (selectedExistingUrl) {
        body.garmentImageUrl = selectedExistingUrl;
      } else {
        body.garmentImageB64 = garmentImageB64;
        body.garmentMimeType = garmentMimeType;
      }

      await apiFetch(`/product-fitting/${selectedProduct.id}`, {
        method: "PUT",
        body: JSON.stringify(body),
      });
      setSuccessMessage(
        t("products.photoSaved", { name: selectedProduct.title })
      );
      setModalOpen(false);
      // Refresh products to update fitting status
      setSearchResults(null);
      loadProducts();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Size chart functions
  const MEASUREMENT_KEYS = ["chest", "waist", "hips", "shoulders", "armLength", "inseam", "neck"];
  const MEASUREMENT_LABELS = {
    chest: t("sizeChart.chest") || "Borst",
    waist: t("sizeChart.waist") || "Taille",
    hips: t("sizeChart.hips") || "Heupen",
    shoulders: t("sizeChart.shoulders") || "Schouders",
    armLength: t("sizeChart.armLength") || "Armlengte",
    inseam: t("sizeChart.inseam") || "Binnenbeenlengte",
    neck: t("sizeChart.neck") || "Nek",
  };

  const openSizeChart = async (product) => {
    setSizeChartProduct(product);
    setSizeChartOpen(true);
    setSizeChartLoading(true);
    setSizeChartInherited(false);
    try {
      const vendorParam = product.vendor ? `?vendor=${encodeURIComponent(product.vendor)}` : "";
      const data = await apiFetch(`/product-fitting/${product.id}/size-chart${vendorParam}`);
      if (data.sizeChart && data.sizeChart.length > 0) {
        setSizeChartData(data.sizeChart);
        setSizeChartInherited(false);
      } else if (data.vendorSizeChart && data.vendorSizeChart.length > 0) {
        setSizeChartData(data.vendorSizeChart);
        setSizeChartInherited(true);
      } else {
        setSizeChartData([]);
        setSizeChartInherited(false);
      }
    } catch {
      setSizeChartData([]);
    } finally {
      setSizeChartLoading(false);
    }
  };

  const addSizeRow = () => {
    setSizeChartData((prev) => [
      ...prev,
      { label: "", measurements: { chest: 0, waist: 0, hips: 0, shoulders: 0, armLength: 0, inseam: 0, neck: 0 } },
    ]);
  };

  const updateSizeLabel = (index, value) => {
    setSizeChartData((prev) => prev.map((row, i) => i === index ? { ...row, label: value } : row));
  };

  const updateSizeMeasurement = (index, key, value) => {
    setSizeChartData((prev) => prev.map((row, i) =>
      i === index ? { ...row, measurements: { ...row.measurements, [key]: Number(value) || 0 } } : row
    ));
  };

  const removeSizeRow = (index) => {
    setSizeChartData((prev) => prev.filter((_, i) => i !== index));
  };

  const saveSizeChart = async () => {
    if (!sizeChartProduct) return;
    setSizeChartSaving(true);
    try {
      await apiFetch(`/product-fitting/${sizeChartProduct.id}/size-chart`, {
        method: "PUT",
        body: JSON.stringify({ sizeChart: sizeChartData }),
      });
      setSuccessMessage(t("sizeChart.saved") || "Maattabel opgeslagen!");
      setSizeChartOpen(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSizeChartSaving(false);
    }
  };

  const handleRemoveFitting = async (productId) => {
    if (!confirm(t("products.confirmDeletePhoto")))
      return;
    try {
      await apiFetch(`/product-fitting/${productId}`, { method: "DELETE" });
      setSuccessMessage(t("products.photoDeleted"));
      loadProducts();
    } catch (err) {
      setError(err.message);
    }
  };

  if (!shopLoading && !connected) {
    return (
      <Page title={t("products.title")}>
        <Layout>
          <Layout.Section>
            <Card>
              <EmptyState
                heading={t("products.notConnected")}
                action={{
                  content: t("products.goToIntegration"),
                  onAction: () => navigate("/integration"),
                }}
                image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
              >
                <p>
                  {t("products.notConnectedDesc")}
                </p>
              </EmptyState>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page title={t("products.title")}>
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setError(null)}>
              {error}
            </Banner>
          </Layout.Section>
        )}
        {successMessage && (
          <Layout.Section>
            <Banner tone="success" onDismiss={() => setSuccessMessage(null)}>
              {successMessage}
            </Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <Card>
            <Box padding="400">
              <BlockStack gap="400">
                <Text variant="bodyMd" tone="subdued">
                  {t("products.description")}
                </Text>

                {/* Search and sort bar */}
                {!loading && products.length > 0 && (
                  <InlineStack gap="300" wrap align="space-between" blockAlign="end">
                    <div style={{ flex: 1, minWidth: 200, maxWidth: 400 }}>
                      <TextField
                        placeholder={t("products.searchPlaceholder")}
                        value={searchQuery}
                        onChange={setSearchQuery}
                        clearButton
                        onClearButtonClick={() => setSearchQuery("")}
                        autoComplete="off"
                        label={t("common.search")}
                        labelHidden
                      />
                    </div>
                    <div style={{ minWidth: 150 }}>
                      <Select
                        label={t("products.sort")}
                        labelHidden
                        options={SORT_OPTIONS}
                        value={sortValue}
                        onChange={setSortValue}
                      />
                    </div>
                    {productTypes.length > 2 && (
                      <div style={{ minWidth: 150 }}>
                        <Select
                          label={t("products.type")}
                          labelHidden
                          options={productTypes}
                          value={typeFilter}
                          onChange={setTypeFilter}
                        />
                      </div>
                    )}
                  </InlineStack>
                )}
              </BlockStack>
            </Box>

            {/* Tabs */}
            {!loading && products.length > 0 && (
              <Tabs tabs={TAB_OPTIONS} selected={selectedTab} onSelect={setSelectedTab} />
            )}

            {loading ? (
              <Box padding="400">
                <InlineStack align="center">
                  <Spinner />
                </InlineStack>
              </Box>
            ) : products.length === 0 ? (
              <EmptyState
                heading={t("products.noProducts")}
                image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
              >
                <p>
                  Er zijn nog geen producten in je Shopify winkel, of de
                  koppeling heeft geen toegang tot producten.
                </p>
              </EmptyState>
            ) : searching ? (
              <Box padding="600">
                <BlockStack gap="200" inlineAlign="center">
                  <Spinner size="small" />
                  <Text variant="bodyMd" tone="subdued" alignment="center">
                    {t("common.search")}...
                  </Text>
                </BlockStack>
              </Box>
            ) : filteredProducts.length === 0 ? (
              <Box padding="600">
                <BlockStack gap="200" inlineAlign="center">
                  <Text variant="bodyMd" tone="subdued" alignment="center">
                    {t("products.noProducts")}
                    {searchQuery ? ` voor "${searchQuery}"` : ""}.
                  </Text>
                  <Button variant="plain" onClick={() => { setSearchQuery(""); setSelectedTab(0); setTypeFilter(""); }}>
                    {t("products.clearFilters")}
                  </Button>
                </BlockStack>
              </Box>
            ) : (
              <>
                <Box paddingInline="400" paddingBlock="200">
                  <Text variant="bodySm" tone="subdued">
                    {filteredProducts.length} {searchResults !== null ? t("products.searchResults") : t("products.ofProducts", { total: products.length })}
                  </Text>
                </Box>
                <ResourceList
                  resourceName={{ singular: "product", plural: "producten" }}
                  items={filteredProducts}
                renderItem={(product) => (
                  <ResourceItem
                    id={product.id}
                    media={
                      <Thumbnail
                        source={
                          product.image ||
                          "https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                        }
                        alt={product.title}
                        size="medium"
                      />
                    }
                    shortcutActions={[
                      {
                        content: product.hasFitting
                          ? t("products.changePhoto")
                          : t("products.uploadPhoto"),
                        onAction: () => openUploadModal(product),
                      },
                      ...(product.hasFitting
                        ? [
                            {
                              content: t("sizeChart.title") || "Maattabel",
                              onAction: () => openSizeChart(product),
                            },
                            {
                              content: t("products.landingPage"),
                              onAction: () =>
                                navigate(
                                  `/products/${product.id}/landing-page`
                                ),
                            },
                            {
                              content: t("products.deletePhoto"),
                              destructive: true,
                              onAction: () =>
                                handleRemoveFitting(product.id),
                            },
                          ]
                        : []),
                    ]}
                  >
                    <BlockStack gap="100">
                      <InlineStack gap="200" blockAlign="center" wrap>
                        <Text variant="bodyMd" fontWeight="bold">
                          {product.title}
                        </Text>
                        <Badge
                          tone={
                            product.hasFitting ? "success" : "info"
                          }
                        >
                          {product.hasFitting
                            ? "Pasfoto aanwezig"
                            : "Geen pasfoto"}
                        </Badge>
                        {product.status === "draft" && (
                          <Badge tone="attention">Concept</Badge>
                        )}
                      </InlineStack>
                      <InlineStack gap="200" wrap>
                        {product.productType && (
                          <Text variant="bodySm" tone="subdued">{product.productType}</Text>
                        )}
                        {product.price && (
                          <Text variant="bodySm" tone="subdued">€{product.price}</Text>
                        )}
                        {product.variantCount > 1 && (
                          <Text variant="bodySm" tone="subdued">{product.variantCount} varianten</Text>
                        )}
                      </InlineStack>
                      {product.hasFitting && product.fittingImageUrl && (
                        <InlineStack gap="200" blockAlign="center">
                          <Thumbnail
                            source={product.fittingImageUrl}
                            alt="Kledingfoto"
                            size="small"
                          />
                          <Text variant="bodySm" tone="subdued">
                            Kledingfoto ingesteld
                          </Text>
                        </InlineStack>
                      )}
                    </BlockStack>
                  </ResourceItem>
                )}
              />
              </>
            )}

            {nextPageInfo && (
              <Box padding="400">
                <InlineStack align="center">
                  <Button
                    onClick={() => loadProducts(nextPageInfo)}
                    loading={loadingMore}
                  >
                    {t("common.loading")}
                  </Button>
                </InlineStack>
              </Box>
            )}
          </Card>
        </Layout.Section>
      </Layout>

      {/* Upload garment photo modal */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={`Kledingfoto — ${selectedProduct?.title || ""}`}
        primaryAction={{
          content: saving ? t("common.saving") : t("common.save"),
          onAction: handleSave,
          loading: saving,
        }}
        secondaryActions={[
          { content: t("common.cancel"), onAction: () => setModalOpen(false) },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {selectedProduct?.images?.length > 0 && (
              <BlockStack gap="200">
                <Text variant="bodyMd" fontWeight="semibold">
                  Bestaande productfoto kiezen
                </Text>
                <Text variant="bodySm" tone="subdued">
                  Klik op een foto uit je Shopify product om deze te gebruiken.
                </Text>
                <InlineStack gap="200" wrap>
                  {selectedProduct.images.map((img) => (
                    <div
                      key={img.id}
                      onClick={() => handleSelectExisting(img.src)}
                      style={{
                        cursor: "pointer",
                        border: selectedExistingUrl === img.src
                          ? "3px solid var(--p-color-border-interactive)"
                          : "3px solid transparent",
                        borderRadius: 8,
                        padding: 2,
                        background: selectedExistingUrl === img.src
                          ? "var(--p-color-bg-surface-selected)"
                          : "transparent",
                      }}
                    >
                      <img
                        src={img.src}
                        alt={`Product foto ${img.position}`}
                        style={{
                          width: 80,
                          height: 80,
                          objectFit: "cover",
                          borderRadius: 6,
                          display: "block",
                        }}
                      />
                    </div>
                  ))}
                </InlineStack>
              </BlockStack>
            )}

            <BlockStack gap="200">
              <Text variant="bodyMd" fontWeight="semibold">
                Of upload een eigen foto
              </Text>
              <Text variant="bodySm" tone="subdued">
                Upload een freigestellte foto van het kledingstuk (zonder
                achtergrond werkt het best). Max. 10 MB.
              </Text>
              <DropZone accept="image/*" type="image" onDrop={(drop, accepted) => {
                handleDropZone(drop, accepted);
                setSelectedExistingUrl(null);
              }}>
                {editPreview && !selectedExistingUrl ? (
                  <div style={{ textAlign: "center", padding: "12px" }}>
                    <img
                      src={editPreview}
                      alt="Preview"
                      style={{
                        maxHeight: 200,
                        maxWidth: "100%",
                        borderRadius: 6,
                      }}
                    />
                  </div>
                ) : (
                  <DropZone.FileUpload
                    actionTitle="Selecteer foto"
                    actionHint="of sleep hier naartoe"
                  />
                )}
              </DropZone>
            </BlockStack>

            {editPreview && (
              <BlockStack gap="200">
                <Text variant="bodySm" fontWeight="semibold">
                  Geselecteerde kledingfoto
                </Text>
                <img
                  src={editPreview}
                  alt="Geselecteerd"
                  style={{
                    maxHeight: 160,
                    maxWidth: "100%",
                    borderRadius: 6,
                    objectFit: "contain",
                  }}
                />
              </BlockStack>
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Size chart modal */}
      <Modal
        open={sizeChartOpen}
        onClose={() => setSizeChartOpen(false)}
        title={`${t("sizeChart.title") || "Maattabel"} — ${sizeChartProduct?.title || ""}`}
        primaryAction={{
          content: sizeChartSaving ? t("common.saving") : t("common.save"),
          onAction: saveSizeChart,
          loading: sizeChartSaving,
        }}
        secondaryActions={[
          { content: t("common.cancel"), onAction: () => setSizeChartOpen(false) },
        ]}
        large
      >
        <Modal.Section>
          {sizeChartLoading ? (
            <InlineStack align="center"><Spinner /></InlineStack>
          ) : (
            <BlockStack gap="400">
              {sizeChartInherited && (
                <Banner tone="info">
                  {t("sizeChart.inheritedFrom") || "Deze maten komen van het merk"} <strong>{sizeChartProduct?.vendor}</strong>. {t("sizeChart.inheritedHint") || "Pas ze aan om een eigen maattabel voor dit product op te slaan."}
                </Banner>
              )}
              <Text variant="bodyMd" tone="subdued">
                {t("sizeChart.description") || "Voer de maten in centimeters in per maat. De AI gebruikt deze tabel om de juiste maat aan te bevelen."}
              </Text>

              {sizeChartData.length > 0 && (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr style={{ borderBottom: "2px solid #e1e3e5" }}>
                        <th style={{ padding: "8px 6px", textAlign: "left", minWidth: 80 }}>{t("sizeChart.sizeLabel") || "Maat"}</th>
                        {MEASUREMENT_KEYS.map((key) => (
                          <th key={key} style={{ padding: "8px 6px", textAlign: "center", minWidth: 70 }}>{MEASUREMENT_LABELS[key]}</th>
                        ))}
                        <th style={{ padding: "8px 6px", width: 40 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {sizeChartData.map((row, idx) => (
                        <tr key={idx} style={{ borderBottom: "1px solid #f1f1f1" }}>
                          <td style={{ padding: "4px 2px" }}>
                            <input
                              type="text"
                              value={row.label}
                              onChange={(e) => updateSizeLabel(idx, e.target.value)}
                              placeholder="48"
                              style={{ width: "100%", padding: "6px 8px", border: "1px solid #ccc", borderRadius: 6, fontSize: 13 }}
                            />
                          </td>
                          {MEASUREMENT_KEYS.map((key) => (
                            <td key={key} style={{ padding: "4px 2px" }}>
                              <input
                                type="number"
                                min="0"
                                value={row.measurements[key] || ""}
                                onChange={(e) => updateSizeMeasurement(idx, key, e.target.value)}
                                placeholder="0"
                                style={{ width: "100%", padding: "6px 4px", border: "1px solid #ccc", borderRadius: 6, fontSize: 13, textAlign: "center" }}
                              />
                            </td>
                          ))}
                          <td style={{ padding: "4px 2px", textAlign: "center" }}>
                            <button
                              onClick={() => removeSizeRow(idx)}
                              style={{ background: "none", border: "none", color: "#d72c0d", cursor: "pointer", fontSize: 16 }}
                              title={t("common.delete")}
                            >
                              ✕
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <Button onClick={addSizeRow}>
                {t("sizeChart.addSize") || "+ Maat toevoegen"}
              </Button>
            </BlockStack>
          )}
        </Modal.Section>
      </Modal>
    </Page>
  );
}
